import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { CreateSalesTransactionRequest } from '@/api/handlers/resources/sales/requests';
import type { CreateSalesTransactionResponse } from '@/api/handlers/resources/sales/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { PosQueryKeys } from '../pos/posQueryKeys';
import { ShiftQueryKeys } from '../shifts/shiftQueryKeys';

/**
 * Mutation behind the POS Pay button. On success it invalidates the POS catalog
 * so the grid refetches the stock the sale just drew down. A *refused* sale's
 * catalog is refreshed by the payment flow instead, not here: a refusal often
 * means the catalog was already wrong, and the mutation cannot tell a stock
 * refusal from a shift one. A refused or failed sale does re-read the seller's
 * shift: the refusal may be `NO_OPEN_SHIFT` because the shift was closed on
 * another sale point, and PAY must gate on the server's answer (SEL-27). The
 * query function never rejects — the caller branches on `result.success` and
 * surfaces the server's 400 `error` text in a toast.
 */
export const useCreateSalesTransactionMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<CreateSalesTransactionResponse>,
        Error,
        CreateSalesTransactionRequest
    >({
        mutationFn: (body) => apiClient.sales.createSalesTransaction(body),
        onSettled: (result) => {
            if (result?.success) {
                void queryClient.invalidateQueries({
                    queryKey: PosQueryKeys.catalog(),
                });
                return;
            }
            void queryClient.invalidateQueries({
                queryKey: ShiftQueryKeys.current(),
            });
        },
    });
};
